import m1 from './Icons/Markers/M1.png';
import m2 from './Icons/Markers/M2.png';
import m3 from './Icons/Markers/M3.png';
import m4 from './Icons/Markers/M4.png';
import m5 from './Icons/Markers/M5.png';
import style from './style';
import { CONCENTRATOR_MODEL_IDS } from '../DetailsPanel/constants';
import amberType from './Icons/amberType.png';
import greenType from './Icons/greenType.png';
import redType from './Icons/redType.png';

const internals = {};

// Wait utility to ensure external scripts are available before using them
function waitFor(conditionFn, { timeout = 20000, interval = 50 } = {}) {
	return new Promise((resolve, reject) => {
		const start = Date.now();
		function check() {
			let ready = false;
			try {
				ready = !!conditionFn();
			} catch (err) {
				ready = false;
			}
			if (ready) return resolve(true);
			if (Date.now() - start >= timeout) {
				return reject(new Error('Timeout waiting for required scripts'));
			}
			setTimeout(check, interval);
		}
		check();
	});
}

export const init = (args) => {
	return new Promise((resolve, reject) => {
		const { node } = args;
		const mapsWaitStart = Date.now();
		const mapsReady =
			typeof window !== 'undefined' && window.google && window.google.maps;
		if (!mapsReady) {
			console.warn('[ConcentratorsMap] Google Maps not ready; waiting…');
		}
		waitFor(
			() =>
				typeof window !== 'undefined' && window.google && window.google.maps,
		)
			.then(() => {
				const mapsWaitMs = Date.now() - mapsWaitStart;
				if (mapsWaitMs > 0) {
					console.info(
						`[ConcentratorsMap] Google Maps ready after ${mapsWaitMs}ms (race avoided).`,
					);
				} else {
					console.info('[ConcentratorsMap] Google Maps ready immediately.');
				}
				const { Map, event } = window.google.maps;

				internals.map = new Map(node, {
					center: {
						lat: 38.399308,
						lng: -100.577004,
					},
					zoom: 4,
					styles: style,
					disableDefaultUI: true,
					zoomControl: true,
					minZoom: 3,
				});

				event.addListenerOnce(internals.map, 'tilesloaded', () => resolve());
			})
			.catch((err) => {
				console.error('Google Maps API not ready during init:', err);
				reject(err);
			});
	});
};

export const createMarkers = async (args) => {
	const { data, headings, onClick, onHover, onMouseOut } = args;
	// Ensure Google Maps and MarkerClusterer are available before proceeding
	const mapsWaitStart = Date.now();
	const mapsReady =
		typeof window !== 'undefined' && window.google && window.google.maps;
	if (!mapsReady) {
		console.warn('[ConcentratorsMap] Markers: Google Maps not ready; waiting…');
	}
	await waitFor(
		() => typeof window !== 'undefined' && window.google && window.google.maps,
	);
	const mapsWaitMs = Date.now() - mapsWaitStart;
	if (mapsReady) {
		console.info(
			'[ConcentratorsMap] Markers: Google Maps already available (pre-loaded).',
		);
	} else if (mapsWaitMs > 0) {
		console.info(
			`[ConcentratorsMap] Markers: Google Maps became ready after ${mapsWaitMs}ms.`,
		);
	} else {
		console.info(
			'[ConcentratorsMap] Markers: Google Maps became available without delay after wait.',
		);
	}

	const mcWaitStart = Date.now();
	const mcReady =
		typeof window !== 'undefined' &&
		window.markerClusterer &&
		window.markerClusterer.MarkerClusterer;
	if (!mcReady) {
		console.warn('[ConcentratorsMap] MarkerClusterer not ready; waiting…');
	}
	await waitFor(
		() =>
			typeof window !== 'undefined' &&
			window.markerClusterer &&
			window.markerClusterer.MarkerClusterer,
		{ timeout: 20000 },
	);
	const mcWaitMs = Date.now() - mcWaitStart;
	if (mcReady) {
		console.info(
			'[ConcentratorsMap] MarkerClusterer already available (pre-loaded).',
		);
	} else if (mcWaitMs > 0) {
		console.info(
			`[ConcentratorsMap] MarkerClusterer became ready after ${mcWaitMs}ms (race avoided).`,
		);
	} else {
		console.info(
			'[ConcentratorsMap] MarkerClusterer became available without delay after wait.',
		);
	}
	const { LatLngBounds } = window.google.maps;
	const ids = headings.indexOf('concentrator_profile_ids');
	const modelIds = headings.indexOf('concentrator_model_ids');
	const latitude = headings.indexOf('latitude');
	const longitude = headings.indexOf('longitude');
	const patientIds = headings.indexOf('patient_id');
	const serialNumberIndex = headings.indexOf('serial_number');
	const errorNameIndex = headings.indexOf('latest_error_name');
	const errorSeverityIndex = headings.indexOf('latest_error_severity');
	const lastConnectedIndex = headings.indexOf('last_connected');
	const bounds = new LatLngBounds();
	const markers = [];

	if (internals.cluster && internals.cluster.markers) {
		console.debug(
			'[ConcentratorsMap] Clearing existing markers from previous cluster…',
		);
		internals.cluster.markers.forEach((marker) => marker.setMap(null));
	}

	internals.cluster = new window.markerClusterer.MarkerClusterer(
		internals.map,
		markers,
		{
			styles: [
				{ url: m1, height: 52, width: 53 },
				{ url: m2, height: 55, width: 56 },
				{ url: m3, height: 65, width: 66 },
				{ url: m4, height: 77, width: 78 },
				{ url: m5, height: 89, width: 90 },
			],
			maxZoom: 12,
			minimumClusterSize: 10,
		},
	);

	function processLargeArrayAsync(array, fn, maxTimePerChunk) {
		maxTimePerChunk = maxTimePerChunk || 200;
		let index = 0;
		function now() {
			return new Date().getTime();
		}
		function doChunk() {
			const startTime = now();
			while (index < array.length && now() - startTime <= maxTimePerChunk) {
				const markersIcon = {
					url: greenType,
					size: new window.google.maps.Size(34, 34),
				};
				const errorSeverity = array[index][errorSeverityIndex];
				if (errorSeverity === 'high') {
					markersIcon.url = redType;
				} else if (errorSeverity === 'medium' || errorSeverity === 'low') {
					markersIcon.url = amberType;
				} else {
					markersIcon.url = greenType;
				}

				const marker = new window.google.maps.Marker({
					position: {
						lat: Number(array[index][latitude]),
						lng: Number(array[index][longitude]),
					},
					icon: markersIcon,
					map: internals.map,
				});
				const id = array[index][ids];
				const modelId = array[index][modelIds];
				const modelName =
					Array.isArray(modelId) && modelId.length > 0
						? CONCENTRATOR_MODEL_IDS[modelId[0]] || ''
						: '';
				const patientId = array[index][patientIds];
				const serialNumber = array[index][serialNumberIndex];
				const errorName = array[index][errorNameIndex];
				const lastConnected = array[index][lastConnectedIndex];
				const row = {
					itemId: id,
					model: modelId,
					modelName,
					patientId,
					serialNumber,
					errorName,
					errorSeverity,
					lastConnected,
				};
				const location = new window.google.maps.LatLng(
					marker.position.lat(),
					marker.position.lng(),
				);
				bounds.extend(location);
				marker.addListener('click', () => onClick(row));
				if (onHover) {
					marker.addListener('mouseover', (event) => {
						onHover(row, event && event.domEvent ? event.domEvent : event);
					});
				}
				if (onMouseOut) {
					marker.addListener('mouseout', (event) => {
						onMouseOut(row, event && event.domEvent ? event.domEvent : event);
					});
				}
				markers.push(marker);
				++index;
			}
			internals.cluster.addMarkers(markers);
			if (index < array.length) {
				setTimeout(doChunk, 1);
			}
		}
		doChunk();
	}

	processLargeArrayAsync(data);

	if (markers.length && internals.firstLoad) {
		const { event } = window.google.maps;
		event.addListener(internals.map, 'bounds_changed', () => {
			if (internals.map.getZoom() > 14) internals.map.setZoom(14);
		});
		console.debug(
			'[ConcentratorsMap] Fitting and panning to bounds for markers…',
		);
		internals.map.fitBounds(bounds);
		internals.map.panToBounds(bounds);
		setTimeout(() => {
			event.clearListeners(internals.map, 'bounds_changed');
		}, 300);
	}
	if (data.length) {
		internals.firstLoad = true;
		console.debug(`[ConcentratorsMap] Added ${markers.length} markers to map.`);
	}
};
